import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Clapperboard,
  Home,
  RotateCcw,
  Sparkles,
  Star,
  Trophy,
} from 'lucide-react';
import PageSeo from '@/seo/PageSeo';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import { Button } from '@/components/ui/button';
import {
  TRY_BEATS,
  TRY_INTRO,
  TRY_REVEAL,
  TEASER_LINES,
  teaserIndexFromChoices,
  SCENE_BACKDROP_CLASSES,
} from '@/data/tryDemoContent';
import {
  trackTryDemoComplete,
  trackTryDemoCta,
  trackTryDemoReplay,
  trackTryDemoStart,
  trackTryDemoStep,
} from '@/lib/gtag';
import { trackEvent } from '@/lib/trackEvent';

const PHASES = {
  intro: 'intro',
  beat: 'beat',
  reveal: 'reveal',
};

const TYPE_MS = 22;
const TYPE_CHUNK = 2;
const CHECKPOINT_MS = 480;
const REDACT_FADE_MS = 900;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return reduced;
}

export default function TryDemoPage() {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const headingRef = useRef(null);
  const completeTracked = useRef(false);
  const typeIntervalRef = useRef(null);

  const [phase, setPhase] = useState(PHASES.intro);
  const [beatIndex, setBeatIndex] = useState(0);
  const [choices, setChoices] = useState([]);
  const [typedLen, setTypedLen] = useState(0);
  const [revealDone, setRevealDone] = useState(false);
  const [pickingLock, setPickingLock] = useState(false);
  const [revealBarsVisible, setRevealBarsVisible] = useState(true);
  const [showStamp, setShowStamp] = useState(false);

  const teaserLine = useMemo(() => {
    const idx = teaserIndexFromChoices(choices);
    return TEASER_LINES[idx];
  }, [choices]);

  const progress = useMemo(() => {
    if (phase === PHASES.intro) return 0;
    if (phase === PHASES.beat) return ((beatIndex + 1) / TRY_BEATS.length) * 100;
    return 100;
  }, [phase, beatIndex]);

  const backdropClass = useMemo(() => {
    if (phase === PHASES.intro) return 'bg-black';
    if (phase === PHASES.beat) return SCENE_BACKDROP_CLASSES[beatIndex] ?? SCENE_BACKDROP_CLASSES[0];
    return 'bg-gradient-to-b from-[#0c0c0f] via-[#12121a] to-black';
  }, [phase, beatIndex]);

  const focusHeading = useCallback(() => {
    requestAnimationFrame(() => headingRef.current?.focus());
  }, []);

  const startFlow = useCallback(() => {
    completeTracked.current = false;
    setShowStamp(false);
    trackTryDemoStart();
    void trackEvent('try_demo_started', { meta: { surface: 'try_demo' } });
    setPhase(PHASES.beat);
    setBeatIndex(0);
    setChoices([]);
    setTypedLen(0);
    setRevealDone(false);
    setPickingLock(false);
    setRevealBarsVisible(true);
    focusHeading();
  }, [focusHeading]);

  const pickChoice = useCallback(
    (bit) => {
      if (pickingLock || phase !== PHASES.beat) return;
      setPickingLock(true);
      trackTryDemoStep(beatIndex + 1, bit);

      window.setTimeout(() => {
        const next = [...choices, bit];
        setChoices(next);
        if (beatIndex >= TRY_BEATS.length - 1) {
          setRevealBarsVisible(true);
          setTypedLen(0);
          setRevealDone(false);
          setPhase(PHASES.reveal);
          focusHeading();
        } else {
          setBeatIndex((i) => i + 1);
          focusHeading();
        }
        setPickingLock(false);
      }, reducedMotion ? 0 : CHECKPOINT_MS);
    },
    [pickingLock, phase, beatIndex, choices, focusHeading, reducedMotion],
  );

  /* Redacted bars → hide, then typewriter */
  useEffect(() => {
    if (phase !== PHASES.reveal) {
      setRevealBarsVisible(true);
      setTypedLen(0);
      if (typeIntervalRef.current) {
        window.clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
      return undefined;
    }

    if (reducedMotion) {
      setRevealBarsVisible(false);
      return undefined;
    }

    setRevealBarsVisible(true);
    const hideBars = window.setTimeout(() => setRevealBarsVisible(false), REDACT_FADE_MS);
    return () => window.clearTimeout(hideBars);
  }, [phase, reducedMotion]);

  useEffect(() => {
    if (phase !== PHASES.reveal) {
      completeTracked.current = false;
      return undefined;
    }
    const line = teaserLine;
    const fireComplete = () => {
      if (completeTracked.current) return;
      completeTracked.current = true;
      trackTryDemoComplete();
      void trackEvent('try_demo_completed', { meta: { surface: 'try_demo' } });
    };

    if (reducedMotion) {
      setTypedLen(line.length);
      setRevealDone(true);
      fireComplete();
      return undefined;
    }

    if (revealBarsVisible) {
      setTypedLen(0);
      return undefined;
    }

    setTypedLen(0);
    let i = 0;
    typeIntervalRef.current = window.setInterval(() => {
      i += TYPE_CHUNK;
      if (i >= line.length) {
        setTypedLen(line.length);
        if (typeIntervalRef.current) window.clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
        setRevealDone(true);
        fireComplete();
      } else {
        setTypedLen(i);
      }
    }, TYPE_MS);

    return () => {
      if (typeIntervalRef.current) {
        window.clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
    };
  }, [phase, teaserLine, reducedMotion, revealBarsVisible]);

  useEffect(() => {
    if (!revealDone) {
      setShowStamp(false);
      return undefined;
    }
    const t = window.setTimeout(() => setShowStamp(true), reducedMotion ? 0 : 500);
    return () => window.clearTimeout(t);
  }, [revealDone, reducedMotion]);

  useEffect(() => {
    const onKey = (e) => {
      if (phase !== PHASES.beat || pickingLock) return;
      if (e.key === '1' || e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        pickChoice(0);
      }
      if (e.key === '2' || e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        pickChoice(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, pickChoice, pickingLock]);

  const replay = useCallback(() => {
    completeTracked.current = false;
    trackTryDemoReplay();
    void trackEvent('cta_clicked', { meta: { surface: 'try_demo', action: 'replay' } });
    setPhase(PHASES.intro);
    setBeatIndex(0);
    setChoices([]);
    setTypedLen(0);
    setRevealDone(false);
    setPickingLock(false);
    setRevealBarsVisible(true);
    setShowStamp(false);
  }, []);

  const goFullReport = useCallback(() => {
    trackTryDemoCta();
    void trackEvent('cta_clicked', { meta: { surface: 'try_demo', action: 'full_report_home' } });
    try {
      sessionStorage.setItem('crc_scrollTo', 'get-started');
    } catch {
      /* ignore */
    }
    navigate('/');
  }, [navigate]);

  const beat = TRY_BEATS[beatIndex];

  return (
    <div className="relative min-h-[100dvh] overflow-hidden text-white">
      <style>{`
        @keyframes try-projector-flicker {
          0%, 100% { opacity: 1; filter: brightness(1); }
          92% { opacity: 0.88; filter: brightness(1.08); }
          94% { opacity: 0.72; filter: brightness(0.92); }
          96% { opacity: 1; }
        }
        @keyframes try-stamp-in {
          0% { transform: rotate(-12deg) scale(1.8); opacity: 0; }
          100% { transform: rotate(-8deg) scale(1); opacity: 1; }
        }
        @keyframes try-scan {
          0% { transform: translateY(-120%); opacity: 0.4; }
          100% { transform: translateY(120%); opacity: 0.15; }
        }
      `}</style>

      <GoogleAnalytics />
      <PageSeo
        title="One-minute Career DNA preview (free) | ChooseRightCareer"
        description="Six quick choices, no sign-up. A short preview of how you think and what might suit you—not your full Career DNA report."
        path="/try"
      />

      {/* Cinematic backdrop */}
      <div
        className={`pointer-events-none absolute inset-0 transition-all [transition-duration:900ms] ease-in-out ${backdropClass}`}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_70%_at_50%_42%,transparent_0%,rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.82)_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden
      />

      <header className="fixed top-0 left-0 right-0 z-30 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-100/90 transition hover:bg-white/10 hover:text-white"
          >
            <Home className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
            Home
          </Link>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-200/90">
            <Clapperboard className="h-3.5 w-3.5" aria-hidden />
            Preview
          </span>
        </div>
      </header>

      <div
        className="fixed left-0 top-0 z-20 h-1 bg-gradient-to-r from-amber-400 via-teal-400 to-violet-500 transition-[width] duration-500 ease-out shadow-[0_0_20px_rgba(45,212,191,0.5)]"
        style={{ width: `${progress}%` }}
        aria-hidden
      />

      <main className="relative z-10 flex min-h-[100dvh] flex-col px-4 pb-[max(5rem,env(safe-area-inset-bottom))] pt-[4.5rem] sm:px-8 sm:pt-24">
        {phase === PHASES.intro && (
          <div className="flex flex-1 flex-col items-center justify-center px-2 text-center">
            <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.35em] text-teal-300/80">
              {TRY_INTRO.studio}
            </p>
            <div
              className="max-w-lg space-y-5"
              style={reducedMotion ? undefined : { animation: 'try-projector-flicker 4s ease-in-out infinite' }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber-200/90">{TRY_INTRO.kicker}</p>
              <h1 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-6xl">
                {TRY_INTRO.title}
              </h1>
              <p className="text-base leading-relaxed text-blue-100/90 sm:text-lg">{TRY_INTRO.subtitle}</p>
              {TRY_INTRO.hint ? <p className="text-sm text-slate-400">{TRY_INTRO.hint}</p> : null}
            </div>
            <div className="mt-10 flex flex-col items-center gap-4">
              <Button
                type="button"
                size="lg"
                onClick={startFlow}
                className="group relative h-16 min-w-[14rem] overflow-hidden rounded-2xl border-2 border-teal-400/50 bg-gradient-to-r from-teal-500 via-emerald-500 to-blue-600 text-lg font-black tracking-wide text-white shadow-[0_0_40px_rgba(20,184,166,0.35)] transition hover:scale-[1.03] hover:shadow-[0_0_55px_rgba(59,130,246,0.4)]"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition duration-700 group-hover:translate-x-full" />
                <span className="relative flex items-center gap-2">
                  {TRY_INTRO.cta}
                  <ArrowRight className="h-6 w-6" aria-hidden />
                </span>
              </Button>
              <p className="max-w-xs text-center text-xs text-slate-500">
              This is just a sample. Your full Career DNA goes much deeper
              </p>
            </div>
          </div>
        )}

        {phase === PHASES.beat && beat && (
          <div
            key={`${beat.id}-${beatIndex}`}
            className={`flex flex-1 flex-col ${reducedMotion ? '' : 'animate-in fade-in slide-in-from-bottom-4 duration-500'}`}
          >
            {/* Game HUD */}
            <div className="mb-6 flex flex-col items-center gap-3 sm:mb-8">
              <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-teal-200/95 backdrop-blur-sm">
                <Trophy className="h-3.5 w-3.5 text-amber-300" aria-hidden />
                Scene {beatIndex + 1} / {TRY_BEATS.length}
              </div>
              <div className="flex gap-1.5" aria-hidden>
                {TRY_BEATS.map((b, i) => (
                  <Star
                    key={b.id}
                    className={`h-5 w-5 transition-all duration-300 ${
                      i < beatIndex
                        ? 'fill-amber-400 text-amber-300 scale-100 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]'
                        : i === beatIndex
                          ? 'fill-amber-200/90 text-amber-100 scale-110 animate-pulse'
                          : 'fill-white/10 text-white/20'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center space-y-8 pb-8">
              <div className="text-center">
                <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-teal-300/75">
                  {beat.eyebrow}
                </p>
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  className="font-display text-2xl font-extrabold leading-snug tracking-tight text-white drop-shadow-md sm:text-4xl"
                >
                  {beat.title}
                </h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                {beat.choices.map((c, idx) => (
                  <button
                    key={c.line}
                    type="button"
                    disabled={pickingLock}
                    onClick={() => pickChoice(idx)}
                    className="group relative overflow-hidden rounded-2xl border-2 border-white/20 bg-gradient-to-b from-white/[0.12] to-white/[0.04] p-6 text-left shadow-xl transition-all duration-200 hover:-translate-y-1 hover:border-teal-300/60 hover:from-white/[0.18] hover:shadow-teal-500/20 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/50 disabled:opacity-60 sm:min-h-[11rem] sm:p-7"
                  >
                    <span
                      className={`absolute left-0 top-0 h-full w-1.5 ${
                        idx === 0 ? 'bg-gradient-to-b from-blue-400 to-cyan-400' : 'bg-gradient-to-b from-rose-400 to-amber-400'
                      }`}
                      aria-hidden
                    />
                    <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl bg-black/40 text-lg font-black text-white ring-1 ring-white/20">
                      {idx === 0 ? 'A' : 'B'}
                    </span>
                    <p className="pr-12 font-display text-lg font-bold leading-snug text-white sm:text-xl">{c.line}</p>
                    <p className="mt-3 text-sm font-medium text-blue-100/85">{c.sub}</p>
                    <p className="mt-5 font-mono text-[10px] font-bold uppercase tracking-wider text-teal-200/70 opacity-0 transition group-hover:opacity-100">
                      {idx === 0 ? 'Key 1' : 'Key 2'} · or tap
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {phase === PHASES.reveal && (
          <div
            className={`flex flex-1 flex-col items-center justify-center py-6 ${reducedMotion ? '' : 'animate-in fade-in zoom-in-95 duration-700'}`}
          >
            {/* Spotlight behind document */}
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 h-[min(90vh,42rem)] w-[min(96vw,48rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-500/15 blur-3xl"
              aria-hidden
            />

            <div className="relative mx-auto w-full max-w-lg">
              {/* Scan line */}
              {!reducedMotion && (
                <div
                  className="pointer-events-none absolute inset-x-4 top-8 z-20 h-8 overflow-hidden rounded opacity-40"
                  aria-hidden
                >
                  <div
                    className="h-full w-full bg-gradient-to-b from-transparent via-teal-400/30 to-transparent"
                    style={{ animation: 'try-scan 2.8s ease-in-out infinite' }}
                  />
                </div>
              )}

              <div
                className="relative overflow-hidden rounded-sm border border-amber-900/25 bg-[#f0ebe3] p-8 text-slate-900 shadow-[0_25px_80px_-12px_rgba(0,0,0,0.65)] ring-1 ring-black/10 sm:p-10"
                role="status"
                aria-live="polite"
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.18]"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
                  }}
                  aria-hidden
                />

                <header className="relative mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-amber-900/20 pb-5">
                  <div>
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-amber-900/80">
                      {TRY_REVEAL.docTitle}
                    </p>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-red-800/90">
                      {TRY_REVEAL.docMeta}
                    </p>
                  </div>
                  <div className="rounded border border-amber-800/30 bg-amber-50/80 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-amber-950/90">
                    Sample
                  </div>
                </header>

                <div
                  className={`relative space-y-3 transition-all duration-700 ${
                    revealBarsVisible ? 'opacity-100 blur-0' : 'pointer-events-none max-h-0 opacity-0 blur-sm'
                  }`}
                >
                  <div className="h-3.5 w-full max-w-md rounded bg-slate-400/55" />
                  <div className="h-3.5 w-[80%] max-w-sm rounded bg-slate-400/45" />
                  <div className="h-3.5 w-3/5 rounded bg-slate-400/35" />
                  <p className="pt-1 font-mono text-[9px] uppercase tracking-wider text-slate-500">
                    {TRY_REVEAL.redactedHint}
                  </p>
                </div>

                <div className={`relative mt-8 ${revealBarsVisible && !reducedMotion ? 'opacity-40' : 'opacity-100'}`}>
                  <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-teal-800">
                    Extract · reading line
                  </p>
                  <p
                    ref={headingRef}
                    tabIndex={-1}
                    className="font-display text-xl font-bold leading-relaxed text-slate-900 sm:text-2xl"
                  >
                    {teaserLine.slice(0, typedLen)}
                    {!reducedMotion && typedLen < teaserLine.length && (
                      <span className="ml-0.5 inline-block h-6 w-0.5 animate-pulse bg-teal-600 align-middle" />
                    )}
                  </p>
                </div>

                <p className="relative mt-8 border-t border-amber-900/15 pt-5 text-sm font-medium text-slate-600">
                  {TRY_REVEAL.footerLine}
                </p>

                {showStamp && (
                  <div
                    className="pointer-events-none absolute -right-2 bottom-16 sm:right-4 sm:bottom-20"
                    style={reducedMotion ? undefined : { animation: 'try-stamp-in 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
                  >
                    <div className="flex h-24 w-24 rotate-[-10deg] items-center justify-center rounded-full border-4 border-red-700/80 bg-red-50/95 text-center shadow-lg">
                      <span className="px-2 font-mono text-[9px] font-black uppercase leading-tight text-red-800">
                        Demo
                        <br />
                        only
                      </span>
                    </div>
                  </div>
                )}

                <div className="relative mt-4 flex items-center gap-2 text-amber-900/50">
                  <Sparkles className="h-4 w-4" aria-hidden />
                  <span className="font-mono text-[9px] uppercase tracking-widest">CRC preview layer</span>
                </div>
              </div>
            </div>

            {revealDone && (
              <div
                className={`relative z-10 mt-10 flex w-full max-w-lg flex-col gap-3 px-1 ${reducedMotion ? '' : 'animate-in fade-in slide-in-from-bottom-6 duration-500'}`}
              >
                <Button
                  type="button"
                  size="lg"
                  onClick={goFullReport}
                  className="h-14 rounded-2xl bg-gradient-to-r from-blue-600 via-teal-600 to-emerald-600 text-lg font-bold text-white shadow-[0_0_30px_rgba(59,130,246,0.35)] hover:from-blue-500 hover:via-teal-500 hover:to-emerald-500"
                >
                  {TRY_REVEAL.ctaPrimary}
                  <ArrowRight className="h-5 w-5" aria-hidden />
                </Button>
                <div className="flex flex-wrap items-center justify-center gap-6 pt-2">
                  <button
                    type="button"
                    onClick={replay}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-teal-200/95 underline-offset-4 hover:text-white hover:underline"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                    {TRY_REVEAL.ctaSecondary}
                  </button>
                  <Link
                    to="/"
                    className="text-sm font-semibold text-blue-200/90 underline-offset-4 hover:text-white hover:underline"
                  >
                    {TRY_REVEAL.exitHome}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
